import { useState } from 'react'
import { motion } from 'framer-motion'
import { Newspaper, Calendar, Award, Megaphone, ArrowRight, Tag } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const categories = [
  { id: 'all', name: 'All', icon: Newspaper },
  { id: 'news', name: 'News', icon: Newspaper },
  { id: 'awards', name: 'Awards', icon: Award },
  { id: 'press', name: 'Press Releases', icon: Megaphone },
]

const mediaItems = [
  {
    id: 1,
    type: 'news',
    title: 'GSM Corporation Wins Major Government Tender for Smart Classroom Project',
    date: '2024-01-15',
    summary: 'GSM Corporation successfully won a ₹2.5 Crore tender for implementing smart classroom solutions across 50+ government schools.',
    category: 'Government',
    image: '/images/media/news-1.jpg',
  },
  {
    id: 2,
    type: 'awards',
    title: 'Excellence in Education Infrastructure Award 2024',
    date: '2024-01-10',
    summary: 'Recognized for outstanding contribution to educational infrastructure development across Maharashtra.',
    category: 'Awards',
    image: '/images/media/award-1.jpg',
  },
  {
    id: 3,
    type: 'press',
    title: 'GSM Corporation Expands Healthcare Solutions Division',
    date: '2024-01-05',
    summary: 'Announcing expansion into healthcare infrastructure with new central kitchen setup for mid-day meal programs.',
    category: 'Healthcare',
    image: '/images/media/press-1.jpg',
  },
  {
    id: 4,
    type: 'news',
    title: 'Partnership with Leading EdTech Companies',
    date: '2023-12-20',
    summary: 'Strategic partnerships formed with top EdTech companies to enhance digital learning solutions.',
    category: 'Partnership',
    image: '/images/media/news-2.jpg',
  },
  {
    id: 5,
    type: 'awards',
    title: 'Best SME Award - Maharashtra Region',
    date: '2023-12-15',
    summary: 'Honored with Best SME Award for consistent growth and excellence in service delivery.',
    category: 'Awards',
    image: '/images/media/award-2.jpg',
  },
  {
    id: 6,
    type: 'press',
    title: '150 Crore Business Milestone Achievement',
    date: '2023-12-01',
    summary: 'GSM Corporation group achieves ₹150+ Crore business ecosystem milestone with 9 group companies.',
    category: 'Milestone',
    image: '/images/media/press-2.jpg',
  },
]

export default function MediaCenter() {
  const [activeCategory, setActiveCategory] = useState('all')

  const filteredItems = activeCategory === 'all'
    ? mediaItems
    : mediaItems.filter(item => item.type === activeCategory)

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-900/20 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Newspaper className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-graphite-300">Media Center</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              News & <span className="gradient-text-gold">Updates</span>
            </h1>
            <p className="text-xl text-graphite-300">
              Stay updated with the latest news, press releases, awards, and announcements
              from GSM Corporation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filter */}
      <section className="py-8 border-y border-white/5">
        <div className="container-premium">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'gold' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className="flex items-center gap-2"
              >
                <cat.icon className="w-4 h-4" />
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="hover" className="h-full overflow-hidden group cursor-pointer">
                  {/* Image */}
                  <div className="relative aspect-video overflow-hidden">
                    <div className="w-full h-full bg-gradient-to-br from-primary-500/30 to-secondary-500/30 flex items-center justify-center">
                      <Newspaper className="w-16 h-16 text-white/30" />
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 rounded-full bg-primary-500/80 text-white text-xs font-medium">
                        {item.category}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs text-graphite-500 mb-3">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(item.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                      <Tag className="w-3 h-3 ml-2" />
                      <span className="capitalize">{item.type}</span>
                    </div>

                    <h3 className="text-lg font-semibold text-white mb-3 group-hover:text-primary-400 transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                    <p className="text-graphite-400 text-sm mb-4 line-clamp-2">{item.summary}</p>

                    <div className="flex items-center gap-2 text-primary-400 group-hover:text-primary-300 transition-colors">
                      <span className="text-sm font-medium">Read More</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center">
            <h2 className="heading-md text-white mb-4">Stay Updated</h2>
            <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter for the latest news, updates, and insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 h-12 px-4 rounded-xl bg-white/5 border border-white/20 text-white placeholder:text-graphite-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <Button variant="gold">Subscribe</Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  )
}